<template>
    <div v-if="isPageReady" class="competitor-financials-container">
        <stock-ticker :activeRoundId="activeRoundId"></stock-ticker>
        <div class="countdown" v-if="shouldShowCountdown">
            <Countdown :deadline="currentDeadline" :countdown-id="'competitor-financials'"></Countdown>
            <div class="remaining-label">remaining</div>
        </div>
        <div class="page-title">
            <h3>Competitor Financials</h3>
            <h3 style="font-weight: 1000; text-decoration: underline;">ROUND {{ roundNum }} <span v-if="selectedTeam">- {{ selectedTeam.name }}</span></h3>
        </div>
        <div><h3>Market Ended {{ marketEndFormatted }}</h3></div>
        <div class="md-layout select-team-container">
            <div class="md-layout"></div>
            <div class="md-layout-item md-large-size-33 md-medium-size-50 md-small-size-75 md-xsmall-size-90">
                <h3>Select Team</h3>
                <div class="custom-select-wrapper">
                    <select class="custom-select" name="basic-dropdown" v-model="selectedTeam">
                    <option v-for="(team, index) in selectableTeams" v-bind:key="team.id" :value="team">{{index + 1}}. {{team.name}}</option>
                    </select>
                </div>
            </div>
            <div class="md-layout"></div>
        </div>
        <div class="md-layout">
            <div class="md-layout"></div>
            <financial-ratios-table class="md-layout-item md-large-size-33 md-medium-size-50 md-small-size-75 md-xsmall-size-90" :semester-id="semesterId" :round-config-id="activeRoundId" :team-id="selectedTeam.id"></financial-ratios-table>
            <div class="md-layout"></div>
        </div>
        <div class="md-layout competitor-financials">
            <div class="collapsible-section">
                <div class="collapsible-header" @click="showBalanceSheet = !showBalanceSheet">
                    <span class="collapsible-arrow" :class="{ expanded: showBalanceSheet }">&#9654;</span>
                    <span>Balance Sheet</span>
                </div>
                <div v-show="showBalanceSheet" class="collapsible-content">
                    <balance-sheet :allow-submission-when-valid-game-config-and-game-state="false" :selected-team="selectedTeam" :active-round-id="activeRoundId"></balance-sheet>
                </div>
            </div>
            <div class="collapsible-section">
                <div class="collapsible-header" @click="showIncomeStatement = !showIncomeStatement">
                    <span class="collapsible-arrow" :class="{ expanded: showIncomeStatement }">&#9654;</span>
                    <span>Income Statement</span>
                </div>
                <div v-show="showIncomeStatement" class="collapsible-content">
                    <income-statement :allow-submission-when-valid-game-config-and-game-state="false" :selected-team="selectedTeam" :active-round-id="activeRoundId"></income-statement>
                </div>
            </div>
            <div class="collapsible-section">
                <div class="collapsible-header" @click="showCashFlow = !showCashFlow">
                    <span class="collapsible-arrow" :class="{ expanded: showCashFlow }">&#9654;</span>
                    <span>Cash Flow</span>
                </div>
                <div v-show="showCashFlow" class="collapsible-content">
                    <cash-flow :allow-submission-when-valid-game-config-and-game-state="false" :selected-team="selectedTeam" :active-round-id="activeRoundId"></cash-flow>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import IncomeStatement from './IncomeStatement.vue';
import CashFlow from './CashFlow.vue';
import BalanceSheet from './BalanceSheet.vue';
import StockTicker from './StockTicker.vue';
import {GameRoundPhase} from "temporary-api-types/apiTypes";
import FinancialRatiosTable from './FinancialRatiosTable.vue';
import { DateTime } from 'luxon';
import Countdown from './Countdown';

export default {
    name: "CompetitorFinancials",
    components: {
        'income-statement': IncomeStatement,
        'cash-flow': CashFlow,
        'balance-sheet': BalanceSheet,
        'stock-ticker': StockTicker,
        'financial-ratios-table': FinancialRatiosTable,
        Countdown
    },
    data() {
        return {
            roundNum: null,
            marketEndFormatted: null,
            selectableTeams: null,
            semesterId: null,
            selectedTeam: null,
            isPageReady: false,
            showBalanceSheet: false,
            showIncomeStatement: false,
            showCashFlow: false
        }
    },
    computed: {
        ...mapGetters([
            'getCurrentCourse',
            'getFormattedDate',
            'getScoreboardData',
            'getCurrentGameState',
            'getNextDeadline'
        ]),
        currentDeadline() {
            return this.getNextDeadline;
        },
        shouldShowCountdown() {
            const nowSeconds = DateTime.local().toSeconds();
            const nextDeadlineSeconds = this.getNextDeadline;
            if(isNaN(nextDeadlineSeconds)) {
                return false;
            }
            // show countdown if time is less than 1 hour
            return nextDeadlineSeconds - nowSeconds <= 3599;
        }
    },
    props: {
        activeRoundId: {
            type: String
        }
    },
    watch: {
        activeRoundId(val) {
          if(val) {
            this.roundNum = this.getCurrentCourse.rounds.map((_round) => _round.id).indexOf(this.activeRoundId) + 1;
          }
        }
    },
    methods: {
        ...mapActions([
            'fetchScoreboardData',
            'getStudentTeam'
        ]),
        async init() {
            let roundIndex = this.getScoreboardData.rounds.findIndex((round) => round.roundId === this.activeRoundId);
            const roundData = this.getScoreboardData.rounds[roundIndex];
            this.selectableTeams = await Promise.all(roundData.teamDatas.map(async (teamData) => {
                const studentTeams = await this.getStudentTeam({
                    teamId: teamData.teamId
                });
                const semester = studentTeams.semester;
                return {
                    totalRoundIncome: teamData.currentRoundIncome,
                    id: teamData.teamId,
                    name: teamData.teamName,
                    semester
                }
            })
            .sort((a, b) => b.totalRoundIncome - a.totalRoundIncome))

            this.selectedTeam = this.selectableTeams[0];
            this.roundNum = this.getCurrentCourse.rounds.map((_round) => _round.id).indexOf(this.activeRoundId) + 1;
            const roundTimings = JSON.parse(JSON.stringify(this.selectedTeam.semester.roundTimings));
            const roundTiming = roundTimings[this.roundNum - 1];

            this.marketEndFormatted = this.getFormattedDate(roundTiming.electionsDeadline, this.selectedTeam.semester.timezone);
        }
    },
    async created() {
        this.loggedInTeamId = this.$route.params.teamId;
        this.semesterId = this.$route.params.semesterId;
        await this.fetchScoreboardData({
            semesterId: this.semesterId
        });

        if(this.activeRoundId) {
            await this.init();
        }
        this.isPageReady = true;
    }
}
</script>

<style scoped>

    h3 {
        color: var(--text-primary);
    }
    .custom-select {
        position: relative;
        display: block;
        width: 100%;
        padding: 10px 36px 10px 14px;
        font-size: 16px;
        color: var(--text-primary, #333);
        background-color: var(--inner-box-color, #fff);
        border: 2px solid #ccc;
        border-radius: 8px;
        outline: none;
        appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
        font-weight: bold;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .custom-select:focus {
        border-color: #5b9bd5;
        box-shadow: 0 0 0 3px rgba(91, 155, 213, 0.25);
    }

    .custom-select:hover {
        border-color: #999;
    }

    .custom-select-wrapper {
        position: relative;
        user-select: none;
        width: 100%;
    }

    .custom-select-wrapper::after {
        content: '▾';
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 18px;
        color: #888;
        pointer-events: none;
    }

    .page-title {
        font-weight: bold;
        font-size: 18px;
        margin-top: 15px;
        margin-bottom: 10px;
    }

    .competitor-financials-container {
        padding-top: 64px;
        padding-bottom: 64px;
    }

    .select-team-container {
        padding-bottom: 20px;
    }

    .competitor-financials {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 32px;
    }

    .collapsible-section {
        width: 100%;
        max-width: 900px;
        margin-bottom: 8px;
    }

    .collapsible-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        cursor: pointer;
        font-weight: bold;
        font-size: 16px;
        color: var(--text-primary);
        background-color: var(--main-box-color, #ededed);
        border-radius: 4px;
        user-select: none;
    }

    .collapsible-header:hover {
        opacity: 0.85;
    }

    .collapsible-arrow {
        display: inline-block;
        transition: transform 0.2s ease;
        font-size: 12px;
    }

    .collapsible-arrow.expanded {
        transform: rotate(90deg);
    }

    .collapsible-content {
        padding-top: 8px;
    }
</style>
